import { useEffect, useState } from 'react';
import api from '../utils/api';
import { CheckCircle2, XCircle, RefreshCw, Camera, Plus } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';

const DEFECT_TYPES = [
  'warping', 'stringing', 'layer_shift', 'spaghetti',
  'surface_defect', 'dimension_error', 'color_issue', 'other',
];

const DEFECT_LABEL = {
  warping: '워핑',
  stringing: '스트링잉',
  layer_shift: '레이어 시프트',
  spaghetti: '스파게티',
  surface_defect: '표면 불량',
  dimension_error: '치수 오류',
  color_issue: '색상 문제',
  other: '기타',
};

const RESULT_LABEL = { pass: '합격', fail: '불합격', rework: '재작업' };
const RESULT_COLOR = {
  pass: 'bg-green-500/20 text-green-400',
  fail: 'bg-red-500/20 text-red-400',
  rework: 'bg-orange-500/20 text-orange-400',
};

function QcModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    jobId: '', printerId: '', result: 'pass',
    defectType: '', severity: '', notes: '',
  });
  const [photos, setPhotos] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/jobs?status=completed&limit=50').then(r => setJobs(r.data.jobs || []));
  }, []);

  const save = async () => {
    if (!form.jobId) return alert('잡 선택 필수');
    setSaving(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
    photos.forEach(p => fd.append('photos', p));
    try {
      await api.post('/qc', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onSaved();
      onClose();
    } catch (e) {
      alert('저장 실패: ' + (e.response?.data?.message || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 max-w-lg w-full">
        <h2 className="text-white font-semibold mb-4">QC 기록 추가</h2>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-400 block mb-1">잡 *</label>
            <select
              value={form.jobId}
              onChange={e => {
                const job = jobs.find(j => j.id === +e.target.value);
                setForm(f => ({ ...f, jobId: e.target.value, printerId: job?.printerId || '' }));
              }}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="">-- 선택 --</option>
              {jobs.map(j => (
                <option key={j.id} value={j.id}>
                  #{j.id} {j.xinvanaOrderNumber} - {j.productName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">결과</label>
            <div className="flex gap-2">
              {['pass', 'fail', 'rework'].map(r => (
                <button
                  key={r}
                  onClick={() => setForm(f => ({ ...f, result: r }))}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                    form.result === r ? RESULT_COLOR[r] : 'bg-gray-800 text-gray-500'
                  }`}
                >
                  {RESULT_LABEL[r]}
                </button>
              ))}
            </div>
          </div>
          {form.result !== 'pass' && (
            <>
              <div>
                <label className="text-xs text-gray-400 block mb-1">불량 유형</label>
                <select
                  value={form.defectType}
                  onChange={e => setForm(f => ({ ...f, defectType: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="">-- 선택 --</option>
                  {DEFECT_TYPES.map(d => <option key={d} value={d}>{DEFECT_LABEL[d]}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">심각도</label>
                <select
                  value={form.severity}
                  onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="">-- 선택 --</option>
                  <option value="minor">경미</option>
                  <option value="major">중간</option>
                  <option value="critical">심각</option>
                </select>
              </div>
            </>
          )}
          <div>
            <label className="text-xs text-gray-400 block mb-1">사진 (최대 5장)</label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={e => setPhotos(Array.from(e.target.files || []).slice(0, 5))}
              className="w-full text-xs text-gray-400"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">메모</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={3}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button
            onClick={onClose}
            className="flex-1 py-2 bg-gray-800 text-gray-400 rounded-lg text-sm"
          >취소</button>
          <button
            onClick={save}
            disabled={saving}
            className="flex-1 py-2 bg-brand text-white rounded-lg text-sm disabled:opacity-50"
          >{saving ? '저장 중...' : '저장'}</button>
        </div>
      </div>
    </div>
  );
}

export default function QcPage() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState('');
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const q = filter ? `?result=${filter}` : '';
    api.get(`/qc${q}`).then(r => { if (!cancelled) setList(r.data); });
    return () => { cancelled = true; };
  }, [filter]);

  const load = () => {
    const q = filter ? `?result=${filter}` : '';
    api.get(`/qc${q}`).then(r => setList(r.data));
  };

  return (
    <div>
      <PageHeader icon={CheckCircle2} title="품질 검사 (QC)">
        <div className="flex gap-1">
          {['', 'pass', 'fail', 'rework'].map(f => (
            <button
              key={f || 'all'}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs ${filter === f ? 'bg-brand text-white' : 'bg-gray-800 text-gray-400'}`}
            >{f ? RESULT_LABEL[f] : '전체'}</button>
          ))}
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-lg text-sm"
        >
          <Plus size={14} />
          QC 기록
        </button>
      </PageHeader>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs border-b border-gray-800">
                <th className="text-left pb-2">날짜</th>
                <th className="text-left pb-2">잡</th>
                <th className="text-left pb-2">프린터</th>
                <th className="text-left pb-2">결과</th>
                <th className="text-left pb-2">불량 유형</th>
                <th className="text-left pb-2">심각도</th>
                <th className="text-left pb-2">사진</th>
                <th className="text-left pb-2">검사자</th>
                <th className="text-left pb-2">메모</th>
              </tr>
            </thead>
            <tbody>
              {list.map(q => (
                <tr key={q.id} className="border-b border-gray-800/50 hover:bg-gray-800/50">
                  <td className="py-2 text-gray-400 text-xs">{dayjs(q.createdAt).format('MM-DD HH:mm')}</td>
                  <td className="py-2 text-gray-300">
                    #{q.job?.id} {q.job?.productName}
                  </td>
                  <td className="py-2 text-gray-400">{q.printer?.name || '-'}</td>
                  <td className="py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${RESULT_COLOR[q.result]}`}>
                      {RESULT_LABEL[q.result]}
                    </span>
                  </td>
                  <td className="py-2 text-gray-400">{q.defectType ? DEFECT_LABEL[q.defectType] : '-'}</td>
                  <td className="py-2 text-gray-400 text-xs">{q.severity || '-'}</td>
                  <td className="py-2">
                    {q.photos?.length > 0 && (
                      <div className="flex gap-1">
                        {q.photos.slice(0, 3).map((p, i) => (
                          <img key={i} src={p} className="w-8 h-8 object-cover rounded" alt="" />
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="py-2 text-gray-500 text-xs">{q.inspector?.name || '-'}</td>
                  <td className="py-2 text-gray-400 text-xs max-w-xs truncate">{q.notes || '-'}</td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr><td colSpan={9} className="py-6 text-center text-gray-600">QC 기록 없음</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && <QcModal onClose={() => setShowModal(false)} onSaved={load} />}
    </div>
  );
}
